import {
  SetTechniqueModal,
  ViraText,
  type DayOfWeek,
  type NotifyScope,
  type TechniqueItemType,
  type TechniqueScheduleMode,
} from "@ksana/vira-ui"
import {
  Dialog,
  DialogContent,
  Snackbar,
  SnackbarCloseReason,
} from "@mui/material"
import { Alert } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"
import { useParams } from "react-router-dom"
import { useRecoilState } from "recoil"
import {
  ClientFragment,
  GetQuestionnaireScheduleNotifyAllDocument,
  GetQuestionnaireScheduleNotifyAllQuery,
  GetQuestionnaireScheduleNotifyAllQueryVariables,
  NudgeFragment,
  QuestionnaireFragment,
  useEditScheduledNudgeMutation,
  useEditScheduledQuestionnaireMutation,
  useGetAllActiveNudgesQuery,
  useGetQuestionnairesQuery,
  useScheduleNudgeMutation,
  useScheduleQuestionnaireMutation,
} from "../../generated/graphql"
import { useClients } from "../../hooks/useClients"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useSession } from "../../hooks/useSession"
import { useUser } from "../../hooks/useUser"
import {
  applyRecurrencePreset,
  defaultClinicianSchedule,
  formatRecurrenceSummary,
  recurrenceSelectOptions,
  type ClinicianSchedule,
  type ClinicianScheduleDay,
  type RecurrencePreset,
} from "../../utils/clinicianSchedule"
import { NudgeDomain, Query, Role, nudgeOrder } from "../../utils/constants"
import { convertMinutesToDaysHoursMinutes } from "../../utils/dateTimeUtils"
import { isNudge, isNudgeSchedule, isScheduleItem } from "../../utils/helpers"
import {
  buildScheduleTimeMutationPayload,
  expirationPartsToMinutes,
} from "../../utils/scheduleMutationPayload"
import { enableScheduledItemTabState } from "../../utils/store"
import { ScheduleItem, UnscheduledItem } from "../../utils/types"
import MobileUpdateDialog from "./MobileUpdateDialog"
import MobileVerifyDialog from "./MobileVerifyDialog"

export interface ScheduleItemModalProps {
  open: boolean
  onClose: () => void
  client: ClientFragment
  canSchedule?: boolean
  initialItemType?: TechniqueItemType
  item?: ScheduleItem | UnscheduledItem
}

export const ScheduleItemModal: React.FC<ScheduleItemModalProps> = ({
  open,
  onClose,
  client,
  canSchedule,
  initialItemType = "nudge",
  item,
}) => {
  const { user } = useUser()
  const { session } = useSession()
  const userId = user.id ?? ""
  const { id: clientId } = useParams<{ id: string }>()
  const graphqlClient = useGraphQLClient()
  const queryClient = useQueryClient()
  const { clients } = useClients()
  const { showBriteFeatures } = useOrganizationFeatureFlags()

  const nudges = useGetAllActiveNudgesQuery(graphqlClient).data?.nudges
  const questionnaires =
    useGetQuestionnairesQuery(graphqlClient).data?.questionnaires

  const [itemType, setItemType] = useState<TechniqueItemType>(initialItemType)
  const [scheduleDraft, setScheduleDraft] = useState<ClinicianSchedule>(() =>
    defaultClinicianSchedule()
  )
  const [domain, setDomain] = useState<NudgeDomain | null>(null)
  const [technique, setTechnique] = useState<NudgeFragment | null>(null)
  const [message, setMessage] = useState("")
  const [questionnaire, setQuestionnaire] =
    useState<QuestionnaireFragment | null>(null)
  const [notifyScope, setNotifyScope] = useState<NotifyScope>("only_me")
  const [containRiskAlert, setContainRiskAlert] = useState(false)
  const [hasPhoneNumber, setHasPhoneNumber] = useState(true)
  const [openMobileUpdateDialog, setOpenMobileUpdateDialog] = useState(false)
  const [openMobileVerifyDialog, setOpenMobileVerifyDialog] = useState(false)
  const [mobile, setMobile] = useState("")
  const [, setEnableScheduledItemTab] = useRecoilState(
    enableScheduledItemTabState
  )

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    severity: "success" | "error" | undefined
    message: string | undefined
  }>({ open: false, severity: undefined, message: undefined })

  const clientFirstName = clients?.find((c) => c.id.data === clientId)
    ?.firstName.data

  const filterBrite = useCallback(
    (list: NudgeFragment[] | undefined) => {
      if (!list) return []
      return list.filter(
        (nudge) => showBriteFeatures || nudge.technique !== "Emotional Cooldown"
      )
    },
    [showBriteFeatures]
  )

  const domainOptions = useMemo(() => {
    const filtered = filterBrite(nudges)
    const domains = Array.from(new Set(filtered.map((n) => n.domain)))
    return nudgeOrder.filter((n) => domains.includes(n))
  }, [filterBrite, nudges])

  const techniqueOptions = useMemo(() => {
    if (!domain) return []
    return (nudges?.filter((n) => n.domain === domain) ?? []).map((n) => ({
      id: n.id,
      label: n.technique ?? "",
    }))
  }, [domain, nudges])

  const questionnaireOptions = useMemo(
    () => questionnaires ?? [],
    [questionnaires]
  )

  const assessmentLabel = questionnaire?.name ?? ""
  const assessmentNames = questionnaireOptions.map((q) => q.name)

  const onSuccess = (data: any) => {
    if (
      data?.update_questionnaire_schedules_by_pk === null ||
      data?.update_nudge_schedules_by_pk === null
    ) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Oops! Something went wrong. Please try again later.",
      })
      return
    }
    queryClient.invalidateQueries({ queryKey: ["NudgesScheduleByUser"] })
    queryClient.invalidateQueries({
      queryKey: ["QuestionnairesScheduleByUser"],
    })
    queryClient.invalidateQueries({ queryKey: [Query.GET_IN_APP_RESOURCES] })
  }

  const onError = () => {
    setSnackbar({
      open: true,
      severity: "error",
      message: "Oops! Something went wrong. Please try again later.",
    })
  }

  const { mutate: scheduleNudge } = useScheduleNudgeMutation(graphqlClient, {
    onSuccess,
    onError,
  })
  const { mutate: scheduleQuestionnaire } = useScheduleQuestionnaireMutation(
    graphqlClient,
    { onSuccess, onError }
  )
  const { mutate: editNudge } = useEditScheduledNudgeMutation(graphqlClient, {
    onSuccess,
    onError,
  })
  const { mutate: editQuestionnaire } = useEditScheduledQuestionnaireMutation(
    graphqlClient,
    { onSuccess, onError }
  )

  useEffect(() => {
    if (item && "questionnaire" in item && isScheduleItem(item)) {
      setContainRiskAlert(item.questionnaire.contains_risk_questions ?? false)
      graphqlClient
        .request<
          GetQuestionnaireScheduleNotifyAllQuery,
          GetQuestionnaireScheduleNotifyAllQueryVariables
        >(GetQuestionnaireScheduleNotifyAllDocument, { id: item.id })
        .then((res) => {
          setNotifyScope(
            res.questionnaire_schedules_by_pk?.notify_connected_practitioners
              ? "all_practitioners"
              : "only_me"
          )
        })
    }
  }, [graphqlClient, item])

  const autoFillName = (text: string) =>
    text.replace(/\[FIRST NAME\]/g, clientFirstName ?? "")

  const loadScheduleFromItem = useCallback(() => {
    if (!item) {
      let draft = defaultClinicianSchedule()
      if (initialItemType === "assessment") {
        draft = {
          ...draft,
          expiration: { days: "0", hours: "24", minutes: "0" },
        }
      }
      setScheduleDraft(draft)
      setItemType(initialItemType)
      setDomain(null)
      setTechnique(null)
      setMessage("")
      setQuestionnaire(null)
      setContainRiskAlert(false)
      setHasPhoneNumber(Boolean(user.phoneNumber))
      setNotifyScope("only_me")
      return
    }

    const at = isScheduleItem(item) ? dayjs(item.scheduled_at) : dayjs()
    let draft = defaultClinicianSchedule()
    draft = {
      ...draft,
      mode: isScheduleItem(item) && !item.one_time ? "recurring" : "one_time",
      date: at.format("YYYY-MM-DD"),
      time: at.format("HH:mm"),
      notifyScope: "only_me",
    }

    if (isScheduleItem(item) && !item.one_time) {
      const days: ClinicianScheduleDay[] = []
      if (item.recurring_sunday) days.push("Su")
      if (item.recurring_monday) days.push("M")
      if (item.recurring_tuesday) days.push("Tu")
      if (item.recurring_wednesday) days.push("W")
      if (item.recurring_thursday) days.push("Th")
      if (item.recurring_friday) days.push("F")
      if (item.recurring_saturday) days.push("Sa")
      draft = applyRecurrencePreset({ ...draft, days }, "custom", days)
    } else {
      draft = applyRecurrencePreset(draft, "none")
    }

    if (isScheduleItem(item)) {
      if (isNudgeSchedule(item)) {
        setDomain(nudgeOrder.find((n) => n === item.nudge.domain) ?? null)
        setTechnique(item.nudge)
        setMessage(item.message)
        setItemType("nudge")
      } else {
        setQuestionnaire(item.questionnaire)
        setItemType("assessment")
        const exp = convertMinutesToDaysHoursMinutes(
          item.minutes_to_expiration ?? 1440
        )
        draft = {
          ...draft,
          expiration: {
            days: String(exp.days),
            hours: String(exp.hours),
            minutes: String(exp.minutes),
          },
        }
      }
    } else if (isNudge(item)) {
      setDomain(nudgeOrder.find((n) => n === item.domain) ?? null)
      setTechnique(item)
      setMessage(autoFillName(item.default_messaging))
      setItemType("nudge")
    } else {
      setQuestionnaire(item)
      setContainRiskAlert(item.contains_risk_questions ?? false)
      setHasPhoneNumber(Boolean(user.phoneNumber))
      setItemType("assessment")
    }

    setScheduleDraft(draft)
  }, [initialItemType, item, user.phoneNumber, clientFirstName])

  useEffect(() => {
    if (open) {
      loadScheduleFromItem()
    }
  }, [open, loadScheduleFromItem])

  useEffect(() => {
    if (
      open &&
      itemType === "assessment" &&
      !questionnaire &&
      questionnaireOptions.length > 0
    ) {
      const first = questionnaireOptions[0]
      setQuestionnaire(first)
      setContainRiskAlert(first.contains_risk_questions ?? false)
    }
  }, [open, itemType, questionnaire, questionnaireOptions])

  const handleClose = () => {
    setEnableScheduledItemTab(true)
    onClose()
  }

  const handleCloseSnackbar = (
    _event: SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") return
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  const save = () => {
    try {
      if (!user?.id) {
        throw new Error("User not set")
      }

      const timePayload = buildScheduleTimeMutationPayload(scheduleDraft)

      if (itemType === "nudge") {
        if (!domain) throw new Error("No domain chosen")
        if (!technique) throw new Error("No technique chosen")
        if (message.trim() === "")
          throw new Error("Include a message to continue")

        if (item && isScheduleItem(item)) {
          editNudge({
            id: item.id,
            nudge_id: technique.id,
            message,
            ...timePayload,
          })
        } else {
          scheduleNudge({
            nudge_id: technique.id,
            practitioner_id: user.id,
            user_id: clientId,
            message,
            ...timePayload,
          })
        }
      } else {
        if (!questionnaire) throw new Error("No questionnaire chosen")
        const expiration = expirationPartsToMinutes(
          scheduleDraft.expiration ?? {
            days: "0",
            hours: "24",
            minutes: "0",
          }
        )
        if (expiration < 0) {
          throw new Error("Expiration time is in the past.")
        }
        if (expiration <= 1) {
          throw new Error("Expiration time must be greater than 1 minute")
        }
        if (expiration > 40320) {
          throw new Error("Expiration time cannot be greater than 28 days")
        }

        const sendToAllTimestamp =
          containRiskAlert && notifyScope === "all_practitioners"
            ? dayjs.utc()
            : null

        if (item && isScheduleItem(item)) {
          editQuestionnaire({
            id: item.id,
            questionnaire_id: questionnaire.id,
            minutes_to_expiration: expiration,
            notify_connected_practitioners: sendToAllTimestamp,
            ...timePayload,
          })
        } else {
          scheduleQuestionnaire({
            questionnaire_id: questionnaire.id,
            practitioner_id: user.id,
            user_id: clientId,
            minutes_to_expiration: expiration,
            notify_connected_practitioners: sendToAllTimestamp,
            ...timePayload,
          })
        }
      }

      setSnackbar({
        open: session?.role !== Role.WEB_ROLE,
        severity: "success",
        message: "Scheduled!",
      })
      setEnableScheduledItemTab(true)
      handleClose()
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: (error as Error).message,
      })
    }
  }

  const mode: TechniqueScheduleMode = scheduleDraft.mode

  const modal = (
    <SetTechniqueModal
      open={open && Boolean(canSchedule)}
      mode={mode}
      showRecurrenceEnds={false}
      onModeChange={(nextMode) => {
        setScheduleDraft((current) => ({
          ...current,
          mode: nextMode,
        }))
      }}
      onClose={handleClose}
      itemType={itemType}
      onItemTypeChange={setItemType}
      assessmentLabel={assessmentLabel}
      assessmentOptions={assessmentNames.length ? assessmentNames : [""]}
      onAssessmentChange={(name) => {
        const next = questionnaireOptions.find((q) => q.name === name) ?? null
        setQuestionnaire(next)
        setContainRiskAlert(next?.contains_risk_questions ?? false)
        setHasPhoneNumber(Boolean(user.phoneNumber))
      }}
      nudgeDomainOptions={domainOptions}
      nudgeDomain={domain}
      onNudgeDomainChange={(value) => {
        if (value !== domain) {
          setTechnique(null)
        }
        setDomain(value as NudgeDomain | null)
      }}
      nudgeTechniqueOptions={techniqueOptions}
      nudgeTechniqueId={technique?.id ?? null}
      onNudgeTechniqueChange={(id) => {
        const next = nudges?.find((n) => n.id === id) ?? null
        setTechnique(next)
        if (next) {
          setMessage(autoFillName(next.default_messaging))
        }
      }}
      nudgeMessage={message}
      onNudgeMessageChange={setMessage}
      selectedDays={(scheduleDraft.days ?? []) as DayOfWeek[]}
      onDaysChange={(days) => {
        setScheduleDraft((current) => ({
          ...current,
          days: days as ClinicianScheduleDay[],
        }))
      }}
      notificationDate={scheduleDraft.date ?? ""}
      onNotificationDateChange={(value) => {
        setScheduleDraft((current) => {
          const next = { ...current, date: value }
          const preset = current.recurrence?.preset ?? "none"
          if (preset === "weekly") {
            return applyRecurrencePreset(next, "weekly")
          }
          return next
        })
      }}
      notificationTime={scheduleDraft.time}
      onNotificationTimeChange={(value) => {
        setScheduleDraft((current) => ({ ...current, time: value }))
      }}
      recurrence={
        scheduleDraft.recurrence ?? {
          preset: "none",
          interval: 1,
          unit: "week",
          ends: { type: "never" },
        }
      }
      onRecurrenceChange={(recurrence) => {
        setScheduleDraft((current) =>
          applyRecurrencePreset(
            {
              ...current,
              recurrence: {
                ...recurrence,
                // Hasura path cannot persist end date/count.
                ends: { type: "never" },
              },
            },
            recurrence.preset as RecurrencePreset,
            recurrence.preset === "custom" ? current.days : undefined
          )
        )
      }}
      recurrenceOptions={recurrenceSelectOptions(scheduleDraft.date)}
      recurrenceSummary={formatRecurrenceSummary(scheduleDraft)}
      notifyScope={notifyScope}
      onNotifyScopeChange={setNotifyScope}
      expirationDays={scheduleDraft.expiration?.days ?? "0"}
      expirationHours={scheduleDraft.expiration?.hours ?? "24"}
      expirationMinutes={scheduleDraft.expiration?.minutes ?? "0"}
      onExpirationChange={(part, value) => {
        setScheduleDraft((current) => {
          const expiration = {
            days: current.expiration?.days ?? "0",
            hours: current.expiration?.hours ?? "24",
            minutes: current.expiration?.minutes ?? "0",
            [part]: value,
          }
          return { ...current, expiration }
        })
      }}
      showRiskWarning={containRiskAlert}
      showMobileNumberPrompt={!hasPhoneNumber}
      onMobileNumberSetupClick={() => setOpenMobileUpdateDialog(true)}
      onSave={save}
    />
  )

  return (
    <>
      {open && !canSchedule ? (
        <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
          <DialogContent sx={{ p: 3 }}>
            <ViraText tone="ink" sx={{ fontSize: 24, fontWeight: 700, mb: 1 }}>
              Scheduling Disabled
            </ViraText>
            <ViraText tone="subtle">Client Not Connected</ViraText>
          </DialogContent>
        </Dialog>
      ) : (
        modal
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <MobileVerifyDialog
        open={openMobileVerifyDialog}
        mobile={mobile}
        userId={userId}
        openMobileUpdateDialog={() => {
          setOpenMobileVerifyDialog(false)
          setOpenMobileUpdateDialog(true)
        }}
        verifySuccess={() => {
          setOpenMobileVerifyDialog(false)
          setHasPhoneNumber(true)
        }}
        closeCallback={() => setOpenMobileVerifyDialog(false)}
      />
      <MobileUpdateDialog
        open={openMobileUpdateDialog}
        mobile={mobile}
        openMobileVerifyDialog={(updateMobile: string) => {
          setOpenMobileUpdateDialog(false)
          if (updateMobile) {
            setMobile(updateMobile)
            setOpenMobileVerifyDialog(true)
          }
        }}
        closeCallback={() => setOpenMobileUpdateDialog(false)}
      />
    </>
  )
}

export default ScheduleItemModal
